import { useState, useEffect, useCallback } from "react";
import { docsClient } from "@/lib/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Clock, Play, Calendar, CheckCircle, XCircle } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { getHumanReadableSchedule, getNextRunTime } from "@/utils/cronHelpers";
import { formatJobSummary } from "@/utils/jobResultFormatter";
import { JOB_TYPE_NAMES } from "@/utils/jobHelpers";
import type { Job } from "@/utils/types";
import { toast } from "sonner";

/**
 * Maintenance schedule from pg_cron via maintenance_schedules view.
 */
interface MaintenanceSchedule {
  jobid: number;
  schedule: string; // Cron expression
  command: string;
  nodename: string;
  nodeport: number;
  database: string;
  username: string;
  active: boolean;
  jobname: string;
}

/**
 * Maps maintenance job names (from pg_cron) to job types (used in jobs table).
 */
const JOBNAME_TO_TYPE: Record<string, string> = {
  "localdocs-content-update": "content_update",
  "localdocs-quality-check": "quality_check",
  "localdocs-cleanup": "cleanup",
  "localdocs-auto-fix": "auto_fix",
};

/**
 * Maps job types to edge function names for "Run Now" functionality.
 */
const JOB_TYPE_TO_FUNCTION: Record<string, string> = {
  content_update: "update-documents",
  quality_check: "quality-check",
  cleanup: "cleanup-old-data",
  auto_fix: "auto-fix-quality-issues",
};

/**
 * Extracts friendly display name from pg_cron jobname.
 */
function getScheduleDisplayName(jobname: string): string {
  const jobType = JOBNAME_TO_TYPE[jobname];
  if (jobType && JOB_TYPE_NAMES[jobType]) {
    return JOB_TYPE_NAMES[jobType];
  }
  // Fallback: format jobname
  return jobname
    .replace("localdocs-", "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Dashboard component showing scheduled maintenance jobs.
 * Displays schedule configuration, last run status, next run time,
 * and "Run Now" buttons to trigger maintenance functions manually.
 */
export function ScheduledJobsDashboard() {
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([]);
  const [lastJobs, setLastJobs] = useState<Record<string, Job>>({});
  const [loading, setLoading] = useState(true);
  const [runningJob, setRunningJob] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setError(null);

      // Load maintenance schedules from pg_cron view
      const { data: scheduleData, error: scheduleError } = await docsClient
        .from("maintenance_schedules")
        .select("*");

      if (scheduleError) {
        console.error("Error loading schedules:", scheduleError);
        setError("Failed to load maintenance schedules");
        return;
      }

      setSchedules(scheduleData || []);

      // Load last job for each maintenance type
      const jobTypes = ["content_update", "quality_check", "cleanup", "auto_fix"];
      const lastJobsMap: Record<string, Job> = {};

      for (const jobType of jobTypes) {
        const { data: jobData } = await docsClient
          .from("jobs")
          .select("*")
          .eq("job_type", jobType)
          .ilike("job_name", "Scheduled%")
          .order("completed_at", { ascending: false })
          .limit(1);

        if (jobData && jobData.length > 0) {
          lastJobsMap[jobType] = jobData[0] as Job;
        }
      }

      setLastJobs(lastJobsMap);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const runMaintenanceNow = async (schedule: MaintenanceSchedule) => {
    const jobType = JOBNAME_TO_TYPE[schedule.jobname];
    if (!jobType) {
      toast.error("Unknown maintenance type");
      return;
    }

    const functionName = JOB_TYPE_TO_FUNCTION[jobType];
    if (!functionName) {
      toast.error("No edge function configured for this maintenance type");
      return;
    }

    setRunningJob(schedule.jobname);

    try {
      const { error } = await docsClient.functions.invoke(functionName, {
        body: {},
      });

      if (error) {
        throw error;
      }

      toast.success(`${getScheduleDisplayName(schedule.jobname)} started successfully`);

      // Refresh data after a short delay to show the new job
      setTimeout(() => loadData(), 2000);
    } catch (err) {
      console.error("Error running maintenance:", err);
      toast.error(`Failed to start ${getScheduleDisplayName(schedule.jobname)}`);
    } finally {
      setRunningJob(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading scheduled maintenance...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">
          <XCircle className="h-8 w-8 mx-auto mb-2" />
          <p>{error}</p>
          <Button variant="outline" size="sm" onClick={loadData} className="mt-4">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (schedules.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No scheduled maintenance jobs configured.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Scheduled Maintenance
        </CardTitle>
        <CardDescription>
          Automated maintenance jobs running on schedule via pg_cron
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {schedules.map((schedule) => {
          const jobType = JOBNAME_TO_TYPE[schedule.jobname];
          const lastJob = jobType ? lastJobs[jobType] : undefined;
          const displayName = getScheduleDisplayName(schedule.jobname);
          const humanSchedule = getHumanReadableSchedule(schedule.schedule);
          const nextRun = getNextRunTime(schedule.schedule);
          const isRunning = runningJob === schedule.jobname;

          return (
            <div
              key={schedule.jobid}
              className="border rounded-lg p-4 space-y-3"
              data-testid={`schedule-card-${schedule.jobname}`}
            >
              {/* Header: Name and Status */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-lg">{displayName}</span>
                </div>
                <Badge variant={schedule.active ? "default" : "secondary"}>
                  {schedule.active ? (
                    <>
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Active
                    </>
                  ) : (
                    <>
                      <XCircle className="h-3 w-3 mr-1" />
                      Inactive
                    </>
                  )}
                </Badge>
              </div>

              {/* Schedule Description */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>{humanSchedule}</span>
              </div>

              {/* Last Run Info */}
              <div className="text-sm">
                <span className="text-muted-foreground">Last run: </span>
                {lastJob ? (
                  <span>
                    {lastJob.completed_at
                      ? formatDistanceToNow(new Date(lastJob.completed_at), { addSuffix: true })
                      : "In progress"}{" "}
                    <span className="text-muted-foreground">
                      &bull; {formatJobSummary(lastJob)}
                    </span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">Never</span>
                )}
              </div>

              {/* Next Run Info */}
              {schedule.active && nextRun && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Next run: </span>
                  <span>{format(nextRun, "PPpp")}</span>
                </div>
              )}

              {/* Run Now Button */}
              <div className="flex justify-end pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => runMaintenanceNow(schedule)}
                  disabled={!schedule.active || isRunning}
                  data-testid={`run-now-btn-${schedule.jobname}`}
                >
                  {isRunning ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Run Now
                    </>
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
